import { Box, Typography, LinearProgress, IconButton } from "@mui/material";
import { useEffect } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  InvoiceAttachment,
  InvoiceEdit,
  InvoicesEditFetch,
  LocalStorageAttachment,
} from "../invoiceAPI";
import {
  fetchAttachmentDownloadUrl,
  uploadInvoiceAttachment,
  deleteAttachment,
  fetchAttachmentUploadUrl,
  getAttachmentsFromLocalStorage,
  selectById,
  updateAttachmentsInLocalStorage,
  refreshInvoiceAttachments,
} from "../invoiceDetailedSlice";
import theme from "../../../theme";
import useBackendPolling from "../../../hooks/useBackendPolling";
import { LOCAL_STORAGE_KEY } from "../constants";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { INVOICE_ATTACHMENT_PREFIX } from "../../../common/helpers/regexes";

interface Props {
  uploadFile: File | undefined;
  clearUpload: () => void;
  invoiceId: string;
  isDeleteAllowed: boolean;
  hideTitle?: boolean;
}

export const InvoiceAttachments = ({
  uploadFile,
  clearUpload,
  invoiceId,
  isDeleteAllowed,
  hideTitle = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { isPolling, startPolling, stopPolling, isTimeout, error } =
    useBackendPolling({
      callback: async () => shouldContinuePolling(),
      delay: 5000,
    });

  const attachments =
    useAppSelector((state) => selectById(state, invoiceId))?.attachments || [];

  const downloadAttachment = (attachment: InvoiceAttachment) => {
    const requestAttachment = { filename: attachment.filename, invoiceId };
    dispatch(fetchAttachmentDownloadUrl(requestAttachment)).then((action) => {
      isFulfilled(action) && window.open(action.payload.url, "_blank");
    });
  };

  const removeAttachment = (filename: string) => {
    dispatch(deleteAttachment({ invoiceId, filename }));
  };

  const shouldContinuePolling = async () => {
    const response: InvoicesEditFetch = await dispatch(
      refreshInvoiceAttachments([invoiceId]),
    ).unwrap();

    response.data.forEach((invoice: InvoiceEdit) => {
      dispatch(getAttachmentsFromLocalStorage(invoiceId));
      const attachmentsInStorage: LocalStorageAttachment[] = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY) || "{}",
      ).filter(
        (attachment: any) =>
          attachment.path === "invoices" &&
          attachment.pathId === invoiceId &&
          attachment.status === "scanning",
      );

      // If all needed attachments are found in the response, stop polling
      const areAttachmentsFinished = attachmentsInStorage?.every((attachment) =>
        invoice.attachments?.find(
          (invoiceAttachment) =>
            invoiceAttachment.filename === attachment.filename &&
            invoiceAttachment.status !== "scanning",
        ),
      );
      if (areAttachmentsFinished) {
        stopPolling();
        dispatch(updateAttachmentsInLocalStorage(invoiceId));
      }
    });
  };

  useEffect(() => {
    if (uploadFile) {
      const attachmentRequest = {
        invoiceId,
        filename: uploadFile.name,
        size: uploadFile.size.toString(),
        content: uploadFile,
      };

      // Scroll page down to attachment list
      const el = document.getElementById("invoice-container");
      el && el.scrollTo({ top: el.scrollHeight });

      dispatch(fetchAttachmentUploadUrl(attachmentRequest)).then((action) => {
        clearUpload();
        if (isFulfilled(action)) {
          dispatch(
            uploadInvoiceAttachment({
              ...attachmentRequest,
              url: action.payload,
            }),
          ).then((action) => {
            isFulfilled(action) &&
              dispatch(getAttachmentsFromLocalStorage(invoiceId));
            !isPolling && startPolling();
          });
        }
      });
    }
  }, [uploadFile, dispatch, clearUpload, invoiceId, isPolling, startPolling]);

  useEffect(() => {
    if (isTimeout || error) {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line
  }, [isTimeout]);

  useEffect(() => {
    // Refresh items in local storage and pass them to the store
    dispatch(updateAttachmentsInLocalStorage(invoiceId));
    dispatch(getAttachmentsFromLocalStorage(invoiceId));

    //when arriving to page, start polling if there are attachments in scanning state
    const attachmentsInStorage: LocalStorageAttachment[] = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}",
    ).filter(
      (attachment: any) =>
        attachment.path === "invoices" &&
        attachment.pathId === invoiceId &&
        attachment.status === "scanning",
    );
    attachmentsInStorage.length && startPolling();
    // eslint-disable-next-line
  }, []);

  return (
    <Box hidden={!attachments.length}>
      {!hideTitle && (
        <Typography
          variant="header"
          fontWeight={700}
          fontSize={14}
          marginBottom={2}
        >
          {t("attachments").toUpperCase()}
        </Typography>
      )}
      {attachments.map((attachment, i) => {
        const isScanning = attachment.status === "scanning";
        const isPending = attachment.status === "pending";
        const isInProgress = isScanning || isPending;
        return (
          <Box
            key={attachment.filename}
            sx={{
              backgroundColor: "#F3F3F7",
              minWidth: "400px",
              maxWidth: "700px",
              padding: "5px 15px",
              marginTop: i > 0 ? "15px" : "0px",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              lineBreak: "anywhere",
            }}
          >
            <Box width="35%">
              <CustomButton
                variant="text"
                color="secondary"
                onClick={() => downloadAttachment(attachment)}
                disabled={isInProgress}
                sx={{
                  height: "35px",
                  borderRadius: "20px",
                  padding: "6px 16px",
                }}
              >
                {attachment.filename.match(INVOICE_ATTACHMENT_PREFIX)
                  ? attachment.filename.slice(15)
                  : attachment.filename}
              </CustomButton>
            </Box>
            <Box sx={{ width: "50px" }}>
              <Typography fontWeight={600}>
                {(attachment.size / 1000).toFixed(1)} {t("kb")}
              </Typography>
            </Box>
            <Box sx={{ width: "40%" }} hidden={!isPending && !isScanning}>
              <LinearProgress />
            </Box>
            <Box color={theme.palette.error.main} sx={{ width: "5%" }}>
              {attachment.status === "failed" && (
                <ErrorOutlineIcon
                  sx={{ display: "flex", alignItems: "center" }}
                />
              )}
            </Box>
            {isDeleteAllowed ? (
              <Box hidden={isInProgress}>
                <IconButton
                  onClick={() => removeAttachment(attachment.filename)}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};
